import React, { useState, useEffect } from 'react';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import './LogModals.css';

interface Animal {
  id: number;
  name: string;
  category: string;
  life_stage: string;
  weight_lbs: number | null;
}

interface WeightLogModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const WeightLogModal: React.FC<WeightLogModalProps> = ({ onClose, onSuccess }) => {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [selectedAnimal, setSelectedAnimal] = useState<number | null>(null);
  const [weight, setWeight] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ points: number; message: string } | null>(null);

  // Tape measure state (for pigs)
  const [useTapeMeasure, setUseTapeMeasure] = useState(false);
  const [heartGirth, setHeartGirth] = useState('');
  const [bodyLength, setBodyLength] = useState('');
  const [calculatingTape, setCalculatingTape] = useState(false);

  useEffect(() => {
    loadAnimals();
  }, []);

  const loadAnimals = async () => {
    try {
      const data = await bridgeClubAPI.getAnimals();
      // Filter to animals that can be weighed (pigs, goats, cattle, sheep)
      const weighableAnimals = data.filter((a: Animal) =>
        ['swine', 'goat', 'sheep', 'cattle'].includes(a.category)
      );
      setAnimals(weighableAnimals);
    } catch (err) {
      setError('Failed to load animals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAnimal || !weight) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await bridgeClubAPI.logWeightForPoints({
        animal_id: selectedAnimal,
        weight_lbs: parseFloat(weight),
      });

      if (response.success) {
        setSuccess({
          points: response.points_earned,
          message: response.message,
        });
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(response.message || 'Failed to log weight');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to log weight');
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'swine': return '🐷';
      case 'goat': return '🐐';
      case 'sheep': return '🐑';
      case 'cattle': return '🐄';
      default: return '🐾';
    }
  };

  // Check if selected animal is a pig
  const selectedAnimalData = animals.find(a => a.id === selectedAnimal);
  const isPig = selectedAnimalData?.category === 'swine';

  // Handle tape weight calculation
  const calculateTapeWeight = async () => {
    if (!heartGirth) return;

    setCalculatingTape(true);
    setError(null);

    try {
      const result = await bridgeClubAPI.calculateTapeWeight({
        heart_girth_inches: parseFloat(heartGirth),
        body_length_inches: bodyLength ? parseFloat(bodyLength) : undefined,
        formula: 'auto',
      });

      if (result.calculated_weight_lbs) {
        setWeight(result.calculated_weight_lbs.toFixed(1));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to calculate weight');
    } finally {
      setCalculatingTape(false);
    }
  };

  // Reset tape fields when switching animals or modes
  const handleAnimalSelect = (animalId: number) => {
    setSelectedAnimal(animalId);
    setUseTapeMeasure(false);
    setHeartGirth('');
    setBodyLength('');
    setWeight('');
  };

  return (
    <div className="log-modal-overlay" onClick={onClose}>
      <div className="log-modal" onClick={e => e.stopPropagation()}>
        <div className="log-modal-header">
          <span className="log-modal-icon">⚖️</span>
          <h2>Log Weight</h2>
          <button className="log-modal-close" onClick={onClose}>&times;</button>
        </div>

        {success ? (
          <div className="log-success">
            <span className="success-icon">✅</span>
            <p className="success-points">+{success.points} points!</p>
            <p className="success-message">{success.message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {loading ? (
              <div className="log-loading">Loading animals...</div>
            ) : animals.length === 0 ? (
              <div className="log-empty">
                <p>No weighable animals found.</p>
                <p className="log-empty-hint">
                  Add pigs, goats, sheep, or cattle to track their weights.
                </p>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Select Animal</label>
                  <div className="animal-select-grid">
                    {animals.map(animal => (
                      <button
                        key={animal.id}
                        type="button"
                        className={`animal-select-btn ${selectedAnimal === animal.id ? 'selected' : ''}`}
                        onClick={() => handleAnimalSelect(animal.id)}
                      >
                        <span className="animal-select-icon">{getCategoryIcon(animal.category)}</span>
                        <span className="animal-select-name">{animal.name || `${animal.category} #${animal.id}`}</span>
                        {animal.weight_lbs && (
                          <span className="animal-select-weight">Last: {animal.weight_lbs} lbs</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tape Weight Calculator Toggle (for pigs only) */}
                {isPig && selectedAnimal && (
                  <div className="form-group">
                    <label>Measurement Method</label>
                    <div className="method-toggle">
                      <button
                        type="button"
                        className={`method-btn ${!useTapeMeasure ? 'active' : ''}`}
                        onClick={() => { setUseTapeMeasure(false); setHeartGirth(''); setBodyLength(''); }}
                      >
                        Enter Weight
                      </button>
                      <button
                        type="button"
                        className={`method-btn ${useTapeMeasure ? 'active' : ''}`}
                        onClick={() => { setUseTapeMeasure(true); setWeight(''); }}
                      >
                        Use Tape Measure
                      </button>
                    </div>
                  </div>
                )}

                {/* Tape Measure Inputs (for pigs with tape measure mode) */}
                {isPig && useTapeMeasure && (
                  <div className="tape-calculator">
                    <div className="tape-hint">
                      Measure around the heart girth (just behind front legs) and optionally the body length.
                    </div>
                    <div className="form-group">
                      <label htmlFor="heartGirth">Heart Girth (inches) *</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        id="heartGirth"
                        value={heartGirth}
                        onChange={e => setHeartGirth(e.target.value)}
                        onBlur={e => {
                          const val = parseFloat(e.target.value) || 0;
                          if (val > 0) setHeartGirth(String(val));
                          else setHeartGirth('');
                        }}
                        placeholder="e.g., 45.5"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="bodyLength">Body Length (inches, optional)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        id="bodyLength"
                        value={bodyLength}
                        onChange={e => setBodyLength(e.target.value)}
                        onBlur={e => {
                          const val = parseFloat(e.target.value) || 0;
                          if (val > 0) setBodyLength(String(val));
                          else setBodyLength('');
                        }}
                        placeholder="e.g., 38.0"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-calculate"
                      onClick={calculateTapeWeight}
                      disabled={!heartGirth || calculatingTape}
                    >
                      {calculatingTape ? 'Calculating...' : 'Calculate Weight'}
                    </button>
                    {weight && (
                      <div className="calculated-result">
                        Estimated: <strong>{weight} lbs</strong>
                      </div>
                    )}
                  </div>
                )}

                {/* Direct Weight Input */}
                {(!isPig || !useTapeMeasure) && (
                  <div className="form-group">
                    <label htmlFor="weight">Current Weight (lbs)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      id="weight"
                      value={weight}
                      onChange={e => setWeight(e.target.value)}
                      onBlur={e => {
                        const val = parseFloat(e.target.value) || 0;
                        if (val > 0) setWeight(String(val));
                        else setWeight('');
                      }}
                      placeholder="Enter weight in pounds"
                      required
                      disabled={!selectedAnimal}
                    />
                  </div>
                )}

                {error && <div className="log-error">{error}</div>}

                <div className="log-modal-actions">
                  <button type="button" className="btn-cancel" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={!selectedAnimal || !weight || submitting}
                  >
                    {submitting ? 'Logging...' : 'Log Weight'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default WeightLogModal;
